import {lazy, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Options, SeriesBarOptions} from "highcharts";

import {useTypeFilteredData} from "../../../../utils/hooks/useTypeFilteredData";
import {formatTooltip} from "../../../../utils/tooltips";
import {colorSets, ColorSetType} from "../../common/app/constants/colors";
import {FilterOfferType} from "../../common/app/constants/enums";
import {IChartProps} from "./Chart";
import {SuspendedChart} from "./SuspendedChart";
import {withRatioData} from "./withRatioData";

export interface IBarChartProps<D> extends IChartProps {
    data: D[];
    fields: string[];
    fieldsLabels?: string[];
    multicolorSet?: ColorSetType;
    offerType?: FilterOfferType;
    unit?: string;
    valueMultiplier?: number;
    chartTitle?: string;
    roundValues?: boolean;
}

const Chart = lazy(() => import("./ChartDefaultExport"));

export const BarChart = <D extends {}>(props: IBarChartProps<D>) => {
    const {
        data,
        fields,
        fieldsLabels,
        multicolorSet,
        offerType,
        unit,
        valueMultiplier = 1,
        chartTitle,
        roundValues
    } = props;

    const {t, i18n} = useTranslation();

    const filteredData = useTypeFilteredData(data, offerType);

    const series: SeriesBarOptions[] = useMemo(() => {
        if (filteredData && filteredData.length > 0) {
            return [
                {
                    type: "bar",
                    borderWidth: 0,
                    borderRadiusTopLeft: 4,
                    borderRadiusTopRight: 4,
                    pointWidth: 8,
                    data: fields.map((field, i) => ({
                        color: multicolorSet ? colorSets[multicolorSet][i] : undefined,
                        name: fieldsLabels ? fieldsLabels[i] : (t(`fields.${field}`) as string),
                        y: filteredData.reduce((acc: number, curr: any) => acc + curr[field], 0) * valueMultiplier
                    }))
                }
            ];
        }

        return [];
    }, [filteredData, i18n.language]);

    const categories = fields.map((field, i) => (fieldsLabels ? fieldsLabels[i] : (t(`fields.${field}`) as string)));

    const options: Options = {
        subtitle: {
            text: chartTitle ?? "",
            align: "left"
        },
        tooltip: {
            formatter: function () {
                return formatTooltip(this, unit, roundValues);
            }
        },
        xAxis: {
            categories: categories,
            visible: true
        },
        yAxis: {
            labels: {
                enabled: true
            },
            visible: true
        }
    };

    return (
        <SuspendedChart>
            <Chart {...props} customOptions={options} series={series} />
        </SuspendedChart>
    );
};

export const RatioBarChart = withRatioData(BarChart);
